// What someone outside the platform sees when they open a share link.
//
// No session, no nav, no way out of the shared folder: the reader is handed a payload that already
// contains everything it may show, including urls minted for this visit and expiring in minutes. If the
// link has been turned off, has expired, or asks for a code, that is all this page ever learns.

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { formatBytes, queryStringToObject } from '/imports/utils';
import { FileMeta } from '/imports/types';
import { css } from '/imports/ui/css';
import { Button } from '/imports/ui/Button';
import { Icon } from '/imports/ui/Icon';
import { Input } from '/imports/ui/Input';
import { PageBody } from '/imports/ui/PageBody';
import { Logo } from '/imports/ui/Logo';
import { FileViewerModal, fileIsViewable } from '/imports/ui/FileViewerModal';
import { DriveSharePayload, driveReadShare } from '/imports/api/drive';
import { driveFileIcon } from '/imports/ui/DriveShared';

const SharedDocumentPageStyled = styled.div`
  max-width: 54rem;
  margin: 0 auto;
  padding: 2rem 1rem;
  .logo { margin-bottom: 1.5rem; }
  h1 { font-size: 1.25rem; margin: 0 0 0.25rem; }
  .sub { color: ${css.grey4}; font-size: 0.85rem; margin-bottom: 1.5rem; }
  .gate { max-width: 22rem; }
  .gate .btn { margin-top: 0.75rem; }
  .gate .wrong { color: ${css.red4}; font-size: 0.82rem; margin-top: 0.4rem; }
  .crumbs { display: flex; flex-wrap: wrap; gap: 0.2rem; align-items: center; font-size: 0.85rem; margin-bottom: 0.5rem; }
  .crumbs button { background: none; border: none; color: ${css.primaryColor}; cursor: pointer; padding: 0.1rem 0.2rem; }
  .crumbs button:disabled { color: ${css.grey1}; cursor: default; font-weight: 600; }
  .crumbs .sep { color: ${css.grey5}; }
  .row {
    display: flex; align-items: center; gap: 0.5rem; width: 100%;
    padding: 0.5rem 0.25rem; border-bottom: 1px solid ${css.grey7}; font-size: 0.9rem;
    background: none; border-left: none; border-right: none; border-top: none; cursor: pointer; text-align: left;
  }
  .row:hover { background: ${css.grey9}; }
  .row .name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .row .size { color: ${css.grey4}; font-size: 0.78rem; font-variant-numeric: tabular-nums; }
  .empty, .error { color: ${css.grey4}; padding: 1.5rem 0; }
  .error { color: ${css.red4}; }
`;

export const SharedDocumentPage = () => {
  const jwt = queryStringToObject().jwt || '';
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [folderId, setFolderId] = useState<string>();
  const [payload, setPayload] = useState<DriveSharePayload>();
  const [challenge, setChallenge] = useState<{ needsCode?: boolean, needsEmail?: boolean, wrong?: boolean, label?: string }>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState<FileMeta>();

  const read = async (args: { code?: string, email?: string, folderId?: string } = {}) => {
    setBusy(true);
    setError(undefined);
    try {
      const answer = await driveReadShare.call({ jwt, folderId, ...args });
      setChallenge(answer.challenge);
      setPayload(answer.payload);
    }
    catch (err: any) {
      setError(err?.reason || err?.message || `${err}`);
      setPayload(undefined);
    }
    finally {
      setBusy(false);
    }
  };

  // The code and the email are held in state rather than re-asked on every step, so walking into a
  // subfolder does not make the visitor type them again.
  useEffect(() => {
    if (jwt) read({ code: code || undefined, email: email || undefined });
    else setError(strings.ShareLinkNotValid());
  }, [folderId]);

  const open = (file: DriveSharePayload['files'][number]) => {
    const meta = { url: file.url, name: file.name, filename: file.name, type: file.type || '', size: file.size || 0, md5: '', token: '' };
    if (fileIsViewable(meta)) setViewing(meta);
    else window.open(file.url, '_blank', 'noreferrer');
  };

  return (
    <SharedDocumentPageStyled>
      <PageBody>
        <div className="logo"><Logo size={3} /></div>
        {error && <div className="error">{error}</div>}

        {!error && challenge && (
          <div className="gate">
            <h1>{challenge.label || strings.SharedWithYou()}</h1>
            {challenge.needsCode && (
              <>
                <div className="sub">{strings.ShareEnterCode()}</div>
                <Input
                  autoFocus
                  placeholder={strings.ShareAccessCode()}
                  value={code}
                  onChange={(value) => setCode(value || '')}
                  onEnter={(value) => read({ code: value || code, email: email || undefined })}
                />
                {challenge.wrong && <div className="wrong">{strings.ShareWrongCode()}</div>}
              </>
            )}
            {challenge.needsEmail && !challenge.needsCode && (
              <>
                <div className="sub">{strings.ShareEnterEmail()}</div>
                <Input
                  autoFocus
                  placeholder={strings.Email()}
                  value={email}
                  onChange={(value) => setEmail(value || '')}
                  onEnter={(value) => read({ code: code || undefined, email: value || email })}
                />
              </>
            )}
            <Button disabled={busy} onClick={() => read({ code: code || undefined, email: email || undefined })}>
              {strings.Open()}
            </Button>
          </div>
        )}

        {!error && payload && (
          <>
            <h1>{payload.label || payload.rootName}</h1>
            <div className="sub">{strings.SharedWithYou()}</div>
            {payload.kind === 'folder' && payload.crumbs.length > 1 && (
              <div className="crumbs">
                {payload.crumbs.map((crumb, index) => (
                  <React.Fragment key={crumb._id}>
                    {!!index && <span className="sep">/</span>}
                    <button
                      type="button"
                      disabled={index === payload.crumbs.length - 1}
                      onClick={() => setFolderId(crumb._id)}
                    >
                      {crumb.name}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}
            {payload.folders.map((folder) => (
              <button type="button" className="row" key={folder._id} onClick={() => setFolderId(folder._id)}>
                <Icon icon="folder" size={17} fill={css.primaryColor} />
                <span className="name">{folder.name}</span>
              </button>
            ))}
            {payload.files.map((file) => (
              <button type="button" className="row" key={file._id} onClick={() => open(file)}>
                <Icon icon={driveFileIcon(file.ext)} size={17} fill={css.grey3} />
                <span className="name">{file.name}</span>
                <span className="size">{formatBytes(file.size || 0)}</span>
              </button>
            ))}
            {!payload.folders.length && !payload.files.length && <div className="empty">{strings.EmptyFolder()}</div>}
          </>
        )}
      </PageBody>
      {viewing && <FileViewerModal file={viewing} downloadName={viewing.name} onClose={() => setViewing(undefined)} />}
    </SharedDocumentPageStyled>
  );
};
